import React, { useState, useMemo } from "react";
import useScrollToTop from "../../hooks/useScollToTop";
import getCategory from "./../../utils/getCategory";
import getWeightCategory from "./../../utils/getWeightCategory";
import { useUser } from "../Home/UserContext";
import { toast, ToastContainer } from "react-toastify";
import Button from "../../components/Button/Button";
import { FaWeight } from "react-icons/fa";
import TableauCategories from "./TableauCategories";
import { useNavigate } from "react-router-dom";
import HeroImage from "../../components/Hero/HeroImage";

/* ===== helpers saison (1er août -> 31 juillet) ===== */
const getCurrentSeasonStartYear = (d = new Date()) => {
  const y = d.getFullYear();
  const m = d.getMonth();
  return m >= 7 ? y : y - 1;
};
const seasonStringFromStartYear = (startYear) => `${startYear}-${startYear + 1}`;

const MonAppliCatePage = () => {
  useScrollToTop();
  const { user } = useUser();
  const navigate = useNavigate();

  const defaultSeason = useMemo(
    () => seasonStringFromStartYear(getCurrentSeasonStartYear()),
    []
  );
  const [saison, setSaison] = useState(defaultSeason);
  const [genre, setGenre] = useState("Masculin");
  const [dateNaissance, setDateNaissance] = useState("");
  const [poids, setPoids] = useState("");
  const [categorieAge, setCategorieAge] = useState("Inconnu");
  const [categoriePoids, setCategoriePoids] = useState("Non pesé(e)");

  // options saison (autour de l’actuelle)
  const seasonOptions = useMemo(() => {
    const start = getCurrentSeasonStartYear();
    return [start - 1, start, start + 1, start + 2].map(seasonStringFromStartYear);
  }, []);

  /* ===== Calcul des catégories ===== */
  const calculerCategories = () => {
    if (!dateNaissance) {
      toast.error("Merci de renseigner la date de naissance.");
      return;
    }
    const p = Number(poids);
    if (Number.isNaN(p) || p <= 0) {
      toast.error("Merci d’indiquer un poids (kg) valide.");
      return;
    }

    // YYYY-MM-DD
    const formattedDate = new Date(dateNaissance).toISOString().slice(0, 10);
    const ageCategory = getCategory(formattedDate, saison, genre);

    let displayAgeCategory = ageCategory;
    if (genre === "Féminin") {
      if (ageCategory === "Mini-Poussin") displayAgeCategory = "Mini-Poussine";
      if (ageCategory === "Poussin") displayAgeCategory = "Poussine";
      if (ageCategory === "Benjamin") displayAgeCategory = "Benjamine";
      if (ageCategory === "Cadet") displayAgeCategory = "Cadette";
    }

    const weightCategory = getWeightCategory(genre, ageCategory, p);
    setCategorieAge(displayAgeCategory);
    setCategoriePoids(weightCategory);
    toast.success("Catégories calculées avec succès !");
  };

  return (
    <div className="w-full pt-[70px] font-satoshi">
      {/* HERO UNIFORMISÉ */}
      <HeroImage
        title="Catégories d’âges et de poids"
        subtitle="Calculateur + tableau officiels — Ligue Nouvelle-Aquitaine"
        imageUrl="/img/fond15.webp"
      >
        {/* Breadcrumbs (optionnels) */}
        <nav className="mt-2 text-xs md:text-sm text-white/90">
          <button
            onClick={() => navigate("/")}
            className="underline hover:opacity-80 transition-opacity"
          >
            Accueil
          </button>{" "}
          &gt; Compétitions &gt; Catégories
        </nav>
      </HeroImage>

      {/* CONTENU */}
      <div className="max-w-[1200px] mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Formulaire */}
          <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm p-6">
            <h3 className="text-xl font-bold mb-5 text-gray-800 dark:text-gray-100 text-center">
              Calculateur
            </h3>

            {/* Saison + Genre */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block mb-1 text-sm text-gray-700 dark:text-gray-300">
                  Saison
                </label>
                <select
                  value={saison}
                  onChange={(e) => setSaison(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {seasonOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1 text-sm text-gray-700 dark:text-gray-300">
                  Genre
                </label>
                <select
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Masculin">Masculin</option>
                  <option value="Féminin">Féminin</option>
                </select>
              </div>
            </div>

            {/* Date + Poids */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block mb-1 text-sm text-gray-700 dark:text-gray-300">
                  Date de naissance
                </label>
                <input
                  type="date"
                  value={dateNaissance}
                  onChange={(e) => setDateNaissance(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm text-gray-700 dark:text-gray-300">
                  Poids (kg)
                </label>
                <input
                  type="number"
                  min="1"
                  step="0.1"
                  value={poids}
                  onChange={(e) => setPoids(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <Button
              text="Calculer"
              icon={FaWeight}
              onClick={calculerCategories}
              className="w-full mb-1 gap-2 text-lg"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              Les catégories sont calculées selon la saison sélectionnée.
            </p>
          </section>

          {/* Résultats */}
          <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm p-6 flex flex-col justify-center">
            <h3 className="text-xl font-bold mb-5 text-gray-800 dark:text-gray-100 text-center">
              Résultats
            </h3>

            <div className="grid grid-cols-1 gap-4">
              <div className="rounded-xl border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/50 p-4 text-center">
                <div className="text-sm text-blue-700 dark:text-blue-300">Genre</div>
                <div className="text-2xl font-extrabold text-blue-800 dark:text-blue-200">
                  {genre}
                </div>
              </div>

              <div className="rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/50 p-4 text-center">
                <div className="text-sm text-amber-700 dark:text-amber-300">Catégorie d’âge</div>
                <div className="text-2xl font-extrabold text-amber-800 dark:text-amber-200">
                  {categorieAge}
                </div>
              </div>

              <div className="rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/50 p-4 text-center">
                <div className="text-sm text-emerald-700 dark:text-emerald-300">Catégorie de poids</div>
                <div className="text-2xl font-extrabold text-emerald-800 dark:text-emerald-200">
                  {categoriePoids}
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Tableau */}
        <div className="mt-10">
          <TableauCategories />
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default MonAppliCatePage;
